<template>

    <div
        class="sentiment-scale"
        :class="rootClass"
        role="img"
        :aria-label="ariaLabel"
        v-tooltip="{ content: tooltip, disabled: !hasScore }">
        <div class="sentiment-scale__layer sentiment-scale__layer--track">
            <span
                v-for="i in barCount"
                :key="`track-${i}`"
                class="sentiment-scale__bar"
                :style="{ height: `${barHeight(i)}px` }" />
        </div>
        <div
            v-if="hasScore"
            class="sentiment-scale__layer sentiment-scale__layer--fill"
            :style="{ clipPath: `inset(0 ${100 - fillPercent}% 0 0)` }">
            <span
                v-for="i in barCount"
                :key="`fill-${i}`"
                class="sentiment-scale__bar"
                :style="{ height: `${barHeight(i)}px` }" />
        </div>
    </div>

</template>

<style scoped lang="scss">

    .sentiment-scale {
        display: inline-grid;
        height: 10px;
        vertical-align: middle;

        &:not(.is-empty) {
            cursor: pointer;
        }

        &__layer {
            align-items: flex-end;
            display: inline-flex;
            gap: 2px;
            grid-area: 1 / 1;
        }

        &__bar {
            border-radius: 1px;
            width: 4px;
        }

        &__layer--track .sentiment-scale__bar {
            background: $color-muted;
            opacity: 0.25;
        }

        &__layer--fill .sentiment-scale__bar {
            opacity: 1;
        }

        &.is-positive .sentiment-scale__layer--fill .sentiment-scale__bar {
            background: $color-success;
        }

        &.is-neutral .sentiment-scale__layer--fill .sentiment-scale__bar {
            background: $color-gold;
        }

        &.is-negative .sentiment-scale__layer--fill .sentiment-scale__bar {
            background: $color-danger;
        }

        &.is-empty .sentiment-scale__layer--track .sentiment-scale__bar {
            opacity: 0.15;
        }

        &.is-outlined {
            align-content: center;
            background: $color-white;
            border: 1px solid $color-rule;
            border-radius: 5px;
            box-sizing: border-box;
            display: inline-grid;
            flex-shrink: 0;
            height: 50px;
            justify-content: center;
            padding: 0 16px;
            width: 112px;

            .sentiment-scale__bar {
                width: 5px;
            }

            .sentiment-scale__layer {
                gap: 3px;
                height: 10px;
            }
        }
    }

</style>

<script>

export default {

    props: {
        barCount: {
            type: Number,
            default: 10,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        score: {
            default: null,
        },
    },

    computed: {

        hasScore() {
            return this.score !== null && this.score !== undefined && !Number.isNaN(Number(this.score));
        },

        fillPercent() {
            if (!this.hasScore) {
                return 0;
            }
            const score = Math.max(0, Math.min(100, Number(this.score)));
            // Keep at least one bar filled so a true 0 still reads as scored (red), not empty.
            const min_fill = 100 / this.barCount;
            return Math.max(min_fill, score);
        },

        toneClass() {
            if (!this.hasScore) {
                return 'is-empty';
            }
            const score = Number(this.score);
            if (score >= 80) {
                return 'is-positive';
            }
            if (score <= 30) {
                return 'is-negative';
            }
            return 'is-neutral';
        },

        rootClass() {
            return {
                [this.toneClass]: true,
                'is-outlined': this.outlined,
            };
        },

        tooltip() {
            if (!this.hasScore) {
                return null;
            }
            const score = `${Math.round(Number(this.score))} / 100`;
            if (this.outlined) {
                return `${this.$t('labels.sentiment')}: ${score}`;
            }
            return score;
        },

        ariaLabel() {
            if (!this.hasScore) {
                return this.$t('labels.sentiment');
            }
            return this.tooltip;
        },

        barMin() {
            return 3;
        },

        barMax() {
            return 10;
        },

    },

    methods: {

        barHeight(index) {
            const min = this.barMin;
            const max = this.barMax;
            if (this.barCount <= 1) {
                return max;
            }
            return min + ((max - min) * (index - 1) / (this.barCount - 1));
        },

    },

}

</script>
